import { useAuth } from '@/context/AuthContext';
import { Node, findChildren, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import {
  type NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react';
import type { CitationFormat } from '@unriddle/db/types';
import Link from 'next/link';
import { md5 } from '../../utils';
import { CitationTooltip } from './citation-tooltip';

const convertToSuperscript = (num: number): string => {
  const superscriptDigits = ['⁰', '¹', '²', '³', '⁴', '⁵', '⁶', '⁷', '⁸', '⁹'];
  return num
    .toString()
    .split('')
    .map((d) => superscriptDigits[Number.parseInt(d)])
    .join('');
};

export interface CitationAttributes {
  citation: {
    id: string;
    url: string;
    title: string | null;
    version: string;
    author?: string | null;
    authors?: string[] | null;
    publishedYear?: string;
    publishedDate?: string;
    order?: number;
  };
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    citation: {
      /**
       * Comments will be added to the autocomplete.
       */
      addCitation: (attributes: CitationAttributes) => ReturnType;
    };
  }
}

export default Node.create({
  name: 'citation',

  group: 'inline',

  inline: true,

  selectable: true,

  addAttributes() {
    return {
      citation: {
        default: {
          url: '',
          title: '',
          author: '',
          publishedDate: '',
          order: 0,
        },
        parseHTML: (element) => {
          const data = element.getAttribute('data-citation');
          if (!data) return null;

          return JSON.parse(data);
        },
        renderHTML: (attributes: CitationAttributes) => ({
          'data-citation': JSON.stringify(attributes.citation),
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'react-component',
      },
    ];
  },
  addCommands() {
    return {
      addCitation:
        (attributes: CitationAttributes) =>
        ({ commands, state }) => {
          const { from, to } = state.selection;
          const text = state.doc.textBetween(from, to, ' ');

          const endOfSentenceIndex = text.search(/(\.|\?|\!)(?!.*[\.\?\!])/);
          const insertPos =
            endOfSentenceIndex !== -1 ? from + endOfSentenceIndex : to;

          return commands.insertContentAt(insertPos, [
            { type: 'text', text: ' ' },
            {
              type: 'citation',
              attrs: attributes,
            },
          ]);
        },
    };
  },

  renderHTML({ HTMLAttributes }) {
    return ['react-component', mergeAttributes(HTMLAttributes)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(CitationView);
  },
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('citation'),
        appendTransaction: (transactions, _oldState, newState) => {
          const { tr, doc } = newState;

          if (transactions.some((tr) => tr.docChanged)) {
            const nodesToTransform = findChildren(doc, (node) => {
              return node.type.name === this.name;
            });

            nodesToTransform.forEach(({ node, pos }, index) => {
              tr.setNodeMarkup(pos, undefined, {
                citation: {
                  ...node.attrs.citation,
                  order: index,
                },
              });
              // tr.setMeta('addToHistory', false);
            });
          }
          return tr;
        },
      }),
    ];
  },
});

const getCitationFormatV1 = (
  {
    title,
    publishedDate,
    publishedYear,
    order,
    authors,
  }: CitationAttributes['citation'],
  citationFormat: CitationFormat,
) => {
  const citationYear =
    publishedYear ??
    (publishedDate ? new Date(publishedDate).getFullYear() : null);
  let authorString = title || 'Unknown Author';

  if (authors?.length) {
    if (authors.length > 3) {
      authorString = `${authors[0]} et al.`;
    } else if (authors.length > 1) {
      authorString =
        // biome-ignore lint/style/useTemplate: <explanation>
        authors?.slice(0, -1).join(', ') +
        ', and ' +
        authors[authors?.length - 1];
    } else {
      authorString = authors[0];
    }
  }

  switch (citationFormat) {
    case 'MLA_9':
      return `(${authorString})`;
    case 'HARVARD':
      return `(${authorString}, ${citationYear || 'no date'})`;
    case 'CSE':
    case 'CHICAGO':
      return `(${authorString} ${citationYear || 'n.d.'})`;
    case 'APA_7': {
      let authorText = 'Unknown Author';
      if (authors?.length) {
        if (authors.length > 3) {
          authorText = `${authors[0]?.split(' ').pop()} et al.`;
        } else if (authors.length > 1) {
          authorText =
            // biome-ignore lint/style/useTemplate: <explanation>
            authors
              .slice(0, -1)
              .map((author) => author?.split(' ').pop())
              .join(', ') +
            ', and ' +
            authors[authors.length - 1]?.split(' ').pop();
        } else {
          authorText = authors[0]?.split(' ').pop() ?? '';
        }
      }
      return `(${authorText}, ${citationYear || 'n.d.'})`;
    }
    case 'ASA':
    case 'ACS':
      return `(${authorString}, ${citationYear || 'n.d.'})`;
    case 'IEEE':
      return `[${(order ?? 0) + 1}]`;
    case 'AMA':
    case 'VANCOUVER': {
      return convertToSuperscript((order ?? 0) + 1);
    }
    default:
      return `(${authorString}, ${citationYear || 'n.d.'})`;
  }
};

const getCitationFormat = (
  {
    author,
    title,
    publishedDate,
    publishedYear,
    order,
  }: CitationAttributes['citation'],
  citationFormat: CitationFormat,
) => {
  const citationYear =
    publishedYear ??
    (publishedDate ? new Date(publishedDate).getFullYear() : null);
  const authorString = (author ?? title) || 'Unknown Author';
  switch (citationFormat) {
    case 'MLA_9':
      return `(${authorString})`;
    case 'HARVARD':
      return `(${authorString}, ${citationYear || 'no date'})`;
    case 'CSE':
    case 'CHICAGO':
      return `(${authorString} ${citationYear || 'n.d.'})`;
    case 'APA_7': {
      let filteredAuthor = 'Unknown Author';
      if (authorString?.includes(' et al.')) {
        filteredAuthor = `${authorString?.split?.(' et al.')[0]?.split?.(' ').pop()} et al.`;
      } else if (authorString?.includes(', and ')) {
        const splitAuthors = authorString?.split?.(', ');
        filteredAuthor = `${splitAuthors[0]?.split?.(' ').pop()}, ${splitAuthors[1]?.split?.(' ').pop()}, and ${splitAuthors[splitAuthors.length - 1]?.split?.(' ').pop()}`;
      } else if (authorString && authorString !== 'Unknown Author') {
        filteredAuthor = authorString?.split?.(' ').pop() ?? '';
      }
      return `(${filteredAuthor}, ${citationYear || 'n.d.'})`;
    }
    case 'ASA':
    case 'ACS':
      return `(${authorString}, ${citationYear || 'n.d.'})`;
    case 'IEEE':
      return `[${(order ?? 0) + 1}]`;
    case 'AMA':
    case 'VANCOUVER': {
      return convertToSuperscript((order ?? 0) + 1);
    }
    default:
      return `(${authorString}, ${citationYear || 'n.d.'})`;
  }
};

export const getCitation = (
  citation: CitationAttributes['citation'],
  citationFormat: CitationFormat,
) => {
  return citation.version === 'v1'
    ? getCitationFormatV1(citation, citationFormat)
    : getCitationFormat(citation, citationFormat);
};

const CitationView = ({ node }: NodeViewProps) => {
  const { citation } = node.attrs as CitationAttributes;
  const citationId = md5(citation.url);

  const { user } = useAuth();

  const getCitationFormatFn =
    citation.version === 'v1' ? getCitationFormatV1 : getCitationFormat;

  const citationFormat = user?.citationFormat ?? 'APA_7';

  const inlineCitation = getCitationFormatFn(citation, citationFormat);

  return (
    <NodeViewWrapper as="span" className="not-prose font-sans underline">
      <CitationTooltip
        content={inlineCitation}
        citation={citation}
        id={citationId}
      >
        <Link href={`#${citationId}`} target="_self">
          {inlineCitation}
        </Link>
      </CitationTooltip>
    </NodeViewWrapper>
  );
};
